import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config.js';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid, differenceInDays } from 'date-fns';
import PTOSummaryCard from './PTOSummaryCard';


const AbsenceHistory = ({ currentUser }) => {
  const [requests, setRequests] = useState([]);
  const [offCampusNotifications, setOffCampusNotifications] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filteredOffCampus, setFilteredOffCampus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('absences'); // 'absences' or 'offcampus'
  const [filters, setFilters] = useState({
    status: 'all',
    year: new Date().getFullYear().toString(),
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [showCancelModal, setShowCancelModal] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  
  const navigate = useNavigate();

  // Fetch absence requests for the current user
  const fetchAbsenceRequests = useCallback(async () => {
    if (!currentUser?.uid || !currentUser?.tenantId) {
      setError('User information is incomplete. Please refresh the page or contact support.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    
    try {
      const q = query(
        collection(db, 'absence_requests'),
        where('requesterUid', '==', currentUser.uid),
        where('tenantId', '==', currentUser.tenantId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const allData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to dates for sorting/filtering
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt),
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : new Date(doc.data().updatedAt)
      }));

      // Separate off-campus notifications from absence requests
      const absenceRequests = allData.filter(req => !req.isOffCampusOnly);
      const offCampusData = allData.filter(req => req.isOffCampusOnly);

      setRequests(absenceRequests);
      setOffCampusNotifications(offCampusData);
      
      console.log(`Loaded ${absenceRequests.length} absence requests and ${offCampusData.length} off-campus notifications`);
    } catch (err) {
      console.error('Error fetching absence requests:', err);
      setError('Failed to load absence requests. Please refresh the page or contact support.');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchAbsenceRequests();
  }, [fetchAbsenceRequests]);

  // Apply filters and sorting
  useEffect(() => {
    // Filter absence requests
    let filteredAbs = [...requests];
    
    if (filters.status !== 'all') {
      filteredAbs = filteredAbs.filter(req => req.status === filters.status);
    }

    if (filters.year !== 'all') {
      const targetYear = parseInt(filters.year);
      filteredAbs = filteredAbs.filter(req => {
        const requestYear = new Date(req.startDate).getFullYear();
        return requestYear === targetYear;
      });
    }

    filteredAbs.sort((a, b) => {
      let aVal, bVal;
      
      switch (filters.sortBy) {
        case 'startDate':
          aVal = new Date(a.startDate);
          bVal = new Date(b.startDate);
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'businessDaysCount':
          aVal = a.businessDaysCount || 0;
          bVal = b.businessDaysCount || 0;
          break;
        default: // createdAt
          aVal = a.createdAt;
          bVal = b.createdAt;
      }

      if (filters.sortOrder === 'desc') {
        return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
      } else {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      }
    });

    setFilteredRequests(filteredAbs);

    // Filter off-campus notifications
    let filteredOff = [...offCampusNotifications];
    
    if (filters.year !== 'all') {
      const targetYear = parseInt(filters.year);
      filteredOff = filteredOff.filter(req => {
        const requestYear = new Date(req.startDate).getFullYear();
        return requestYear === targetYear;
      });
    }

    filteredOff.sort((a, b) => {
      let aVal = a.startDate;
      let bVal = b.startDate;
      
      if (filters.sortOrder === 'desc') {
        return bVal > aVal ? 1 : bVal < aVal ? -1 : 0;
      } else {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      }
    });

    setFilteredOffCampus(filteredOff);
  }, [requests, offCampusNotifications, filters]);

  // Get unique years from all requests for filter dropdown
  const availableYears = [...new Set([
    ...requests.map(req => new Date(req.startDate).getFullYear()),
    ...offCampusNotifications.map(req => new Date(req.startDate).getFullYear())
  ])].sort((a, b) => b - a);

  // Get status badge styling
  const getStatusBadge = (status) => {
    const styles = {
      pending: { backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' },
      approved: { backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' },
      denied: { backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' },
      cancelled: { backgroundColor: '#e2e3e5', color: '#6c757d', border: '1px solid #d6d8db' }
    };

    const style = styles[status] || styles.pending;

    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 'bold',
        textTransform: 'capitalize',
        ...style
      }}>
        {status}
      </span>
    );
  };

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Invalid Date';
    
    try {
      const start = parseISO(startDate);
      const end = parseISO(endDate);
      
      if (!isValid(start) || !isValid(end)) return 'Invalid Date';
      
      if (startDate === endDate) {
        return format(start, 'MMM d, yyyy');
      } else {
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
      }
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format time for off-campus notifications
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hour, minute] = timeString.split(':');
      const date = new Date();
      date.setHours(parseInt(hour), parseInt(minute));
      return format(date, 'h:mm a');
    } catch (error) {
      return timeString;
    }
  };

  // Cancel a pending request
  const cancelRequest = async (requestId) => {
    setIsCancelling(true);
    try {
      await updateDoc(doc(db, 'absence_requests', requestId), {
        status: 'cancelled',
        updatedAt: Timestamp.now(),
        cancelledBy: currentUser.uid,
        cancelledAt: Timestamp.now()
      });

      setSuccessMessage('Request cancelled successfully.');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh the requests
      fetchAbsenceRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      setError('Failed to cancel request. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setIsCancelling(false);
      setShowCancelModal(null);
    }
  };

  // Calculate summary statistics
  const stats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    denied: requests.filter(r => r.status === 'denied').length,
    totalBusinessDays: requests
      .filter(r => r.status === 'approved')
      .reduce((sum, r) => sum + (r.businessDaysCount || 0), 0),
    offCampusCount: offCampusNotifications.length
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>Absence History</h1>
        <div style={{ padding: '40px' }}>
          <div style={{ fontSize: '18px', marginBottom: '20px' }}>Loading your absence requests...</div>
          <div style={{ color: '#6c757d' }}>Please wait while we fetch your data.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
            Absence & Off-Campus History
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
            View and manage your absence requests and off-campus notifications
          </p>
        </div>
        <button
          onClick={() => navigate('/request-absence')}
          style={{
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '12px 20px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          + New Request
        </button>
      </div>

{/* PTO Summary */}
      <PTOSummaryCard currentUser={currentUser} />

      {/* Messages */}
      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {successMessage && (
        <div style={{ 
          backgroundColor: '#d4edda', 
          color: '#155724', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #c3e6cb'
        }}>
          {successMessage}
        </div>
      )}

      {/* Summary Stats */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '18px' }}>Absence Requests</h3>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff' }}>{stats.total}</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '18px' }}>Pending</h3>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#856404' }}>{stats.pending}</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '18px' }}>Approved</h3>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745' }}>{stats.approved}</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '18px' }}>Days Used</h3>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6c757d' }}>{stats.totalBusinessDays}</div>
        </div>
        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '18px' }}>Off-Campus</h3>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>{stats.offCampusCount}</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '20px',
        borderBottom: '2px solid #dee2e6',
        paddingBottom: '0'
      }}>
        <button
          onClick={() => setActiveTab('absences')}
          style={{
            backgroundColor: activeTab === 'absences' ? '#007bff' : 'transparent',
            color: activeTab === 'absences' ? 'white' : '#6c757d',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginBottom: '-2px',
            borderBottom: activeTab === 'absences' ? '2px solid #007bff' : 'none'
          }}
        >
          Absence Requests ({stats.total})
        </button>
        <button
          onClick={() => setActiveTab('offcampus')}
          style={{
            backgroundColor: activeTab === 'offcampus' ? '#17a2b8' : 'transparent',
            color: activeTab === 'offcampus' ? 'white' : '#6c757d',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            marginBottom: '-2px',
            borderBottom: activeTab === 'offcampus' ? '2px solid #17a2b8' : 'none'
          }}
        >
          Off-Campus Notifications ({stats.offCampusCount})
        </button>
      </div>

      {/* Filters */}
      {activeTab === 'absences' && (
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '20px', 
          marginBottom: '20px' 
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
            Filters & Sorting
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Year
              </label>
              <select
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="all">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="createdAt">Date Submitted</option>
                <option value="startDate">Absence Start Date</option>
                <option value="status">Status</option>
                <option value="businessDaysCount">Days Requested</option>
              </select>
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Sort Order
              </label>
              <select
                value={filters.sortOrder}
                onChange={(e) => setFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'offcampus' && (
        <div style={{ 
          backgroundColor: 'white', 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '20px', 
          marginBottom: '20px' 
        }}>
          <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold', color: '#495057' }}>
            Filter by Year
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Year
              </label>
              <select
                value={filters.year}
                onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="all">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px' }}>
                Sort Order
              </label>
              <select
                value={filters.sortOrder}
                onChange={(e) => setFilters(prev => ({ ...prev, sortOrder: e.target.value }))}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Absence Requests Table */}
      {activeTab === 'absences' && (
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
          {filteredRequests.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📅</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#495057' }}>No absence requests found</h3>
              <p style={{ margin: 0 }}>
                {requests.length === 0 
                  ? "You haven't submitted any absence requests yet."
                  : "No requests match your current filters."}
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Date Range
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Reason
                  </th>
                  <th style={{ padding: '15px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Days
                  </th>
                  <th style={{ padding: '15px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Status
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Submitted
                  </th>
                  <th style={{ padding: '15px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request, index) => (
                  <tr key={request.id} style={{ 
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    borderBottom: '1px solid #dee2e6'
                  }}>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {formatDateRange(request.startDate, request.endDate)}
                      </div>
                      {request.needsSubCoverage && (
                        <small style={{ color: '#007bff', fontWeight: 'bold' }}>
                          Sub Coverage Needed
                        </small>
                      )}
                    </td>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {request.reason}
                      </div>
                      {request.reasonOther && (
                        <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '2px' }}>
                          {request.reasonOther}
                        </div>
                      )}
                      {request.notes && (
                        <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', fontStyle: 'italic' }}>
                          "{request.notes}"
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                        {request.days || 'N/A'}
                      </div>
                      <small style={{ color: '#6c757d' }}>business days</small>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center', verticalAlign: 'top' }}>
                      {getStatusBadge(request.status)}
                    </td>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '14px' }}>
                        {request.createdAt ? format(request.createdAt, 'MMM d, yyyy') : 'Unknown'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                        {request.createdAt ? format(request.createdAt, 'h:mm a') : ''}
                      </div>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center', verticalAlign: 'top' }}>
                      {request.status === 'pending' && (
                        <button
                          onClick={() => setShowCancelModal(request)}
                          disabled={isCancelling}
                          style={{
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '12px',
                            cursor: isCancelling ? 'not-allowed' : 'pointer',
                            opacity: isCancelling ? 0.6 : 1
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Off-Campus Notifications Table */}
      {activeTab === 'offcampus' && (
        <div style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}>
          {filteredOffCampus.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚗</div>
              <h3 style={{ margin: '0 0 10px 0', color: '#495057' }}>No off-campus notifications found</h3>
              <p style={{ margin: 0 }}>
                You haven't submitted any off-campus notifications yet.
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Date
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Time Away
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Reason
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Notes
                  </th>
                  <th style={{ padding: '15px', textAlign: 'center', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Status
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontWeight: 'bold' }}>
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredOffCampus.map((notification, index) => (
                  <tr key={notification.id} style={{ 
                    backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                    borderBottom: '1px solid #dee2e6'
                  }}>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {format(parseISO(notification.startDate), 'MMM d, yyyy')}
                      </div>
                      <div style={{ fontSize: '12px', color: '#17a2b8', marginTop: '4px' }}>
                        Off-Campus
                      </div>
                    </td>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold', color: '#17a2b8' }}>
                        {formatTime(notification.departureTime)} - {formatTime(notification.returnTime)}
                      </div>
                    </td>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      <div style={{ fontWeight: 'bold' }}>
                        {notification.reason}
                      </div>
                      {notification.reasonOther && (
                        <div style={{ fontSize: '14px', color: '#6c757d', marginTop: '2px' }}>
                          {notification.reasonOther}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      {notification.notes ? (
                        <div style={{ fontSize: '14px', color: '#6c757d', fontStyle: 'italic' }}>
                          "{notification.notes}"
                        </div>
                      ) : (
                        <span style={{ color: '#6c757d', fontSize: '14px' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center', verticalAlign: 'top' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        backgroundColor: '#d1ecf1',
                        color: '#0c5460',
                        border: '1px solid #bee5eb'
                      }}>
                        Auto-Approved
                      </span>
                    </td>
                    <td style={{ padding: '15px', verticalAlign: 'top' }}>
                      <div style={{ fontSize: '14px' }}>
                        {notification.createdAt ? format(notification.createdAt, 'MMM d, yyyy') : 'Unknown'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>
                        {notification.createdAt ? format(notification.createdAt, 'h:mm a') : ''}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#333' }}>Cancel Absence Request?</h3>
            <p style={{ margin: '0 0 20px 0', color: '#666', lineHeight: '1.5' }}>
              Are you sure you want to cancel your absence request for{' '}
              <strong>{formatDateRange(showCancelModal.startDate, showCancelModal.endDate)}</strong>?
              <br /><br />
              This action cannot be undone. You will need to submit a new request if you change your mind.
            </p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCancelModal(null)}
                disabled={isCancelling}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  cursor: isCancelling ? 'not-allowed' : 'pointer',
                  opacity: isCancelling ? 0.6 : 1
                }}
              >
                Keep Request
              </button>
              <button
                onClick={() => cancelRequest(showCancelModal.id)}
                disabled={isCancelling}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  fontWeight: 'bold',
                  cursor: isCancelling ? 'not-allowed' : 'pointer',
                  opacity: isCancelling ? 0.6 : 1
                }}
              >
                {isCancelling ? 'Cancelling...' : 'Yes, Cancel Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbsenceHistory;